import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { visitorApiInstance } from '@api/generated/axios-instance';
import { useEffectiveTenant } from './useEffectiveTenant';

/**
 * Wire shape of `GET /api/v1/Tenant` / `GET /api/v1/Tenant/{id}` (`TenantDto`, camelCase).
 * The orval `Tenant` type models `receptions` as objects, which does not match the wire.
 */
export interface TenantSummary {
  id: string;
  name: string;
  domain: string;
  isDefault: boolean;
  receptions: string[];
}

export const TENANT_CATALOG_QUERY_KEY = ['tenantCatalog'] as const;

/**
 * Every tenant — only for a platform_admin (Phase 51 D-15: the list endpoint is platform_admin
 * only). Disabled for everyone else, so no request is ever sent.
 */
export function useAllTenants(): UseQueryResult<TenantSummary[]> {
  const { effectiveTenantId, isPlatformAdmin } = useEffectiveTenant();
  return useQuery({
    queryKey: [...TENANT_CATALOG_QUERY_KEY, 'all', effectiveTenantId],
    queryFn: () => visitorApiInstance<TenantSummary[]>({ url: '/api/v1/Tenant', method: 'GET' }),
    enabled: isPlatformAdmin,
  });
}

/**
 * The tenants the caller may pick from: all tenants for a platform_admin, only the caller's own
 * tenant (`GET /api/v1/Tenant/{id}`) for everyone else — never the list-all endpoint.
 *
 * `options.enabled: false` sends no request at all — for screens that also render for roles which
 * may not read tenants (e.g. contractor_manager, while both tenant endpoints require `admin`).
 */
export function useSelectableTenants(options: { enabled?: boolean } = {}): UseQueryResult<TenantSummary[]> {
  const { enabled = true } = options;
  const { effectiveTenantId, isPlatformAdmin } = useEffectiveTenant();
  return useQuery({
    queryKey: isPlatformAdmin
      ? [...TENANT_CATALOG_QUERY_KEY, 'all', effectiveTenantId]
      : [...TENANT_CATALOG_QUERY_KEY, 'own', effectiveTenantId],
    queryFn: async () => {
      if (isPlatformAdmin) {
        return visitorApiInstance<TenantSummary[]>({ url: '/api/v1/Tenant', method: 'GET' });
      }
      const own = await visitorApiInstance<TenantSummary>({
        url: `/api/v1/Tenant/${effectiveTenantId}`,
        method: 'GET',
      });
      return [own];
    },
    enabled: enabled && (isPlatformAdmin || !!effectiveTenantId),
  });
}
